import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Typography, CircularProgress, Snackbar, Button, Tabs, Tab, Box, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Business, Error as ErrorIcon, SwapHoriz, Sync } from '@material-ui/icons'
import stockService from '../../services/StockService'
import StockActuel from '../StockActuel'
import MouvementsStock from '../MouvementsStock'

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    backgroundColor: '#ffebee',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #f44336'
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: '#f44336'
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

// Hook personnalisé pour gérer le cache des données
const useDataCache = () => {
  const cacheRef = useRef({
    stock: { data: [], timestamp: 0 },
    mouvements: { data: [], timestamp: 0 }
  })

  const getCachedData = (key) => {
    const cached = cacheRef.current[key]
    if (cached && Date.now() - cached.timestamp < 30000) { // Cache de 30 secondes
      return cached.data
    }
    return null
  }

  const setCachedData = (key, data) => {
    cacheRef.current[key] = {
      data: data || [], // S'assurer que c'est toujours un tableau
      timestamp: Date.now()
    }
  }

  const clearCache = () => {
    cacheRef.current = {
      stock: { data: [], timestamp: 0 },
      mouvements: { data: [], timestamp: 0 }
    }
  }

  return { getCachedData, setCachedData, clearCache }
}

function GestionStock() {
  const classes = useStyles()
  const { getCachedData, setCachedData, clearCache } = useDataCache()
  
  // Initialiser avec des tableaux vides pour éviter undefined
  const [stockData, setStockData] = useState([])
  const [mouvementsData, setMouvementsData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  
  // Référence pour suivre les requêtes en cours
  const pendingRequestsRef = useRef(new Set())
  
  const tabs = [
    { key: 'stock', label: '📦 Stock Actuel', icon: <Business /> },
    { key: 'mouvements', label: '📈 Mouvements', icon: <SwapHoriz /> },
  ]

  // Fonction pour récupérer les données avec cache et déduplication
  const fetchData = useCallback(async (tabIndex) => {
    const requestKey = `${tabIndex}`
    
    // Éviter les requêtes en double
    if (pendingRequestsRef.current.has(requestKey)) {
      return
    }
    
    pendingRequestsRef.current.add(requestKey)
    
    try {
      setLoading(true)
      setError(null)

      let response
      const cacheKey = tabs[tabIndex].key

      // Vérifier le cache d'abord
      const cachedData = getCachedData(cacheKey)
      if (cachedData) {
        console.log(`📦 Utilisation du cache pour ${cacheKey}`)
        response = { data: { data: cachedData } }
      } else {
        console.log(`🔄 Chargement onglet ${tabIndex}`)

        switch (cacheKey) {
          case 'stock':
            response = await stockService.getStockActuel(1, 1000)
            if (response?.data) {
              setCachedData('stock', response.data.data || response.data)
            }
            break

          case 'mouvements': 
            response = await stockService.getHistoriqueMouvements({ 
              page: 1, 
              limit: 10000,
              type: 'all' 
            })
            console.log('📊 Réponse API mouvements:', response)
            
            if (response?.data) {
              const mouvementsData = response.data.mouvements || response.data.data || response.data
              console.log('📊 Données mouvements extraites:', mouvementsData)
              setCachedData('mouvements', mouvementsData)
            }
            break

          default:
            break
        }
      }

      // Gérer la structure de réponse différente pour chaque type
      let data
      if (cacheKey === 'mouvements') {
        data = response?.data?.mouvements || response?.data?.data || response?.data || []
      } else {
        data = response?.data?.data || response?.data || []
      }

      console.log(`📊 Données finales pour ${cacheKey}:`, data.length, 'éléments')

      switch (cacheKey) {
        case 'stock':
          setStockData(Array.isArray(data) ? data : [])
          break
        case 'mouvements':
          setMouvementsData(Array.isArray(data) ? data : [])
          break
        default:
          break
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      if (error.name !== 'AbortError') {
        setError('Erreur lors du chargement des données: ' + (error.response?.data?.message || error.message))
        setSnackbarMessage('Erreur lors du chargement des données')
        setSnackbarOpen(true)
      }
    } finally {
      pendingRequestsRef.current.delete(requestKey)
      setLoading(false)
    }
  }, [tabs, getCachedData, setCachedData])

  // Chargement initial optimisé
  useEffect(() => {
    let mounted = true
    
    const loadInitialData = async () => {
      if (mounted) {
        await fetchData(activeTab)
      }
    }

    loadInitialData()

    return () => {
      mounted = false
      pendingRequestsRef.current.clear()
    }
  }, [activeTab, fetchData])

  // Gestionnaire de synchronisation optimisé
  const handleSynchroniser = useCallback(async () => {
    try {
      setLoading(true)
      clearCache()
      await stockService.synchroniserStock()
      await fetchData(activeTab)
      setSnackbarMessage('Stock synchronisé avec succès')
      setSnackbarOpen(true)
    } catch (error) {
      setSnackbarMessage('Erreur lors de la synchronisation')
      setSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }, [activeTab, fetchData, clearCache])

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleRetry = useCallback(() => {
    fetchData(activeTab)
  }, [activeTab, fetchData])

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue)
  }, [])

  // Afficher le loading seulement si pas de données
  const showInitialLoading = loading && 
    ((activeTab === 0 && (!stockData || stockData.length === 0)) || 
     (activeTab === 1 && (!mouvementsData || mouvementsData.length === 0)))

  if (showInitialLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1">
          Chargement des données...
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" gutterBottom style={{ marginBottom: '30px', color: 'gray' }}>
        📦 Gestion des Stocks et Défauts
      </Typography>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        {tabs.map((tab, index) => (
          <Tab 
            key={tab.key}
            icon={tab.icon}
            label={tab.label}
            id={`tab-${index}`}
          />
        ))}
      </Tabs>

      {error && (
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="body1" style={{ flex: 1 }}>
            {error}
          </Typography>
          <Button color="secondary" onClick={handleRetry}>
            Réessayer
          </Button>
        </div>
      )}

      {/* Boutons d'action */}
      <Box display="flex" justifyContent="flex-end" mb={2} gap={1}>
        <Tooltip title="Synchroniser le stock">
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleSynchroniser}
            disabled={loading}
          >
            {loading ? 'Synchronisation...' : 'Synchroniser'}
          </Button>
        </Tooltip>
      </Box>

      {/* Contenu des onglets */}
      {activeTab === 0 && (
        <StockActuel
          stockData={stockData || []}
          allStockData={stockData || []}
          loading={loading}
          onRefresh={() => fetchData(0)}
          onSnackbar={setSnackbarMessage}
        />
      )}

      {activeTab === 1 && (
        <MouvementsStock
          mouvementsData={mouvementsData || []}
          loading={loading}
          onRefresh={() => fetchData(1)} 
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
            Fermer
          </Button>
        }
      />
    </div>
  )
}

export default React.memo(GestionStock)